import { captureException } from '@sentry/react';
import { useCallback, useRef, useState } from 'react';
import useSWR, { type Fetcher, type SWRConfiguration, useSWRConfig } from 'swr';
import type { MutationFetcher } from 'swr/mutation';
import useSWRMutation from 'swr/mutation';

import { useActionItemsRefresh } from '~/contexts/actionItemsRefreshContext';
import {
  invoicesAdjustmentsLinkCreate,
  invoicesAdjustmentsUnlinkCreate,
  invoicesDestroy,
  invoicesGenerateInsightsCreate,
  invoicesNotesCreate,
  invoicesNotesList,
  invoicesPartialUpdate,
  invoicesRetrieve,
} from '~/data/generated/endpoints/invoices/invoices';
import { useRefetchedOnOpen } from '~/data/hooks/useRefetchedOnOpen';
import { creditLinksMutator, getInvoiceMutator } from '~/data/hooks/utils';
import { LiftAPIClientError } from '~/data/liftApiMutator';
import { parseApiResponse } from '~/data/parseApiResponse';
import type {
  API_InvoiceUpdateRequest,
  ApproveInvoiceForm,
  Invoice,
  InvoiceNote,
  InvoiceWriteResponse,
  PartialApproveInvoiceForm,
  RejectInvoiceForm,
  ResetInvoiceForm,
} from '~/data/schemas/invoice';
import {
  InvoiceActionType,
  InvoiceNoteSchema,
  invoiceSchema,
  InvoiceStatus,
  invoiceWriteResponseSchema,
} from '~/data/schemas/invoice';
import useLAISnackbar from '~/hooks/useLAISnackbar';

const invoiceFetcher: Fetcher<Invoice, readonly ['invoice', invoiceId: string]> = async ([
  _,
  invoiceId,
]) => {
  const { data } = await invoicesRetrieve(invoiceId);
  // invoiceSchema coerces the DRF decimal-string money fields to numbers, so
  // parsing here replaces the manual coerceInvoiceNumerics pass on this path.
  return parseApiResponse(invoiceSchema, data, 'Invoice');
};

const invoiceUpdateFetcher: MutationFetcher<
  InvoiceWriteResponse,
  readonly ['invoice', invoiceId: string],
  Partial<API_InvoiceUpdateRequest>
> = async ([_, invoiceId], { arg }) => {
  // Invoice update is a PATCH, so it maps to invoicesPartialUpdate. The hook
  // still works in terms of the hand-written API_InvoiceUpdateRequest (camelCase
  // payload unchanged), tightened relative to the generated body, so cast at
  // this boundary (AMP-503).
  const { data } = await invoicesPartialUpdate(
    invoiceId,
    arg as unknown as Parameters<typeof invoicesPartialUpdate>[1],
  );

  // The PATCH response is the (sparser) write serializer, not the full read
  // Invoice, so it is parsed through its own generated-derived schema
  // (`invoiceWriteResponseSchema`), which folds in the DRF decimal-string → number
  // coercions the manual `coerceInvoiceNumerics` pass used to apply here. Callers
  // revalidate the full invoice afterwards, so the leaner write shape is fine
  // (AMP-585).
  return parseApiResponse(invoiceWriteResponseSchema, data, 'Invoice');
};

const invoiceDeleteFetcher: MutationFetcher<void, readonly [key: string], { id: string }> = async (
  [key],
  { arg: { id } },
) => {
  await invoicesDestroy(id);
};

const invoiceNotesFetcher: Fetcher<
  InvoiceNote[],
  readonly ['invoice', invoiceId: string, 'notes']
> = async ([_, invoiceId]) => {
  const { data } = await invoicesNotesList(invoiceId);
  const results = Array.isArray(data) ? data : data?.results;
  return parseApiResponse(InvoiceNoteSchema.array(), results, 'InvoiceNote');
};

const invoiceAddNoteFetcher: MutationFetcher<
  void,
  readonly ['invoice', invoiceId: string, 'notes'] | null,
  { consultantId: string; content: string }
> = async ([_, invoiceId], { arg: { consultantId, content } }) => {
  // `author` carries the consultant id. NoteCreateUpdateSerializer declares
  // `invoice` as a required UUIDField and the viewset does not inject it from
  // the nested route, so the body must send it explicitly or the request 400s.
  await invoicesNotesCreate(invoiceId, {
    invoice: invoiceId,
    author: consultantId,
    content,
    isLiftAi: false,
  });
};

const useReviewInvoice = (invoiceId: string | null) => {
  const { mutate } = useSWRConfig();
  const { refresh: refreshActionItems } = useActionItemsRefresh();

  const { trigger } = useSWRMutation(
    invoiceId ? (['invoice', invoiceId] as const) : null,
    invoiceUpdateFetcher,
    {},
  );

  const reviewInvoice = useCallback(
    async (
      invoiceActionType: InvoiceActionType,
      formData?:
        | RejectInvoiceForm
        | PartialApproveInvoiceForm
        | ResetInvoiceForm
        | ApproveInvoiceForm,
    ) => {
      if (!invoiceId) {
        throw new Error('Invoice ID is required to update an invoice');
      }

      const dateStamped = new Date().toISOString(); // QUESTION(zachary): Should we trust the client's time?

      let updateDataBody: Partial<API_InvoiceUpdateRequest>;

      switch (invoiceActionType) {
        case InvoiceActionType.APPROVE:
          updateDataBody = {
            ...formData,
            id: invoiceId,
            status: InvoiceStatus.Approved,
            dateStamped,
          };
          break;
        case InvoiceActionType.REJECT:
          updateDataBody = {
            ...formData,
            id: invoiceId,
            status: InvoiceStatus.Rejected,
            dateStamped,
            reviewedAmount: 0,
          };
          break;
        case InvoiceActionType.PARTIAL:
          updateDataBody = {
            ...formData,
            id: invoiceId,
            status: InvoiceStatus.PartialApproval,
            dateStamped,
          };
          break;
        case InvoiceActionType.REQUEST_DATA:
          updateDataBody = { id: invoiceId, status: InvoiceStatus.PendingData };
          break;
        case InvoiceActionType.RESET:
          updateDataBody = {
            id: invoiceId,
            status: InvoiceStatus.Reset,
            reason: formData?.reason,
          };
          break;
        default:
          throw new Error(`Invalid invoice action type: ${invoiceActionType}`);
      }

      await trigger(updateDataBody, { revalidate: false });

      // Await revalidation so callers see fresh data (e.g. ConfirmationDialog),
      // but don't propagate failures — the API update already succeeded.
      await mutate(getInvoiceMutator(invoiceId)).catch(() => {});
      // The action may have resolved this invoice's Backlog item (e.g. it's no
      // longer pending), so refresh the action-items list and sidebar badge.
      refreshActionItems();
    },
    [invoiceId, mutate, trigger, refreshActionItems],
  );

  return reviewInvoice;
};

const useUpdateRelatedInvoice = (invoiceId: string | null) => {
  const { mutate } = useSWRConfig();

  const { trigger } = useSWRMutation(
    invoiceId ? (['invoice', invoiceId] as const) : null,
    invoiceUpdateFetcher,
  );

  const addRelatedInvoice = useCallback(
    async (relatedInvoices: string[]) => {
      await trigger({ relatedInvoices }, { revalidate: false });

      void mutate(getInvoiceMutator(invoiceId));
    },
    [invoiceId, mutate, trigger],
  );

  return addRelatedInvoice;
};

const useInvoiceDelete = () => {
  const { mutate } = useSWRConfig();

  const { trigger } = useSWRMutation(['invoice'], invoiceDeleteFetcher);

  const deleteInvoice = useCallback(
    async (invoiceId: string) => {
      if (!invoiceId) {
        throw new Error('Invoice ID is required to update an invoice');
      }

      await trigger({ id: invoiceId }, { revalidate: false });

      void mutate(getInvoiceMutator(invoiceId));
      void mutate(creditLinksMutator);
    },
    [mutate, trigger],
  );

  return deleteInvoice;
};

const useAddInvoiceNote = (invoiceId: string | null) => {
  const { mutate } = useSWRConfig();

  const { trigger } = useSWRMutation(
    invoiceId ? (['invoice', invoiceId, 'notes'] as const) : null,
    invoiceAddNoteFetcher,
    {},
  );

  const addInvoiceNote = useCallback(
    async (consultantId: string, content: string) => {
      if (!consultantId) {
        throw new Error('consultant ID is required to add a note');
      }

      await trigger({ consultantId, content }, { revalidate: false });

      void mutate(getInvoiceMutator(invoiceId));
    },
    [invoiceId, mutate, trigger],
  );

  return addInvoiceNote;
};

const useLinkAdjustment = (invoiceId: string | null) => {
  const { mutate } = useSWRConfig();

  const linkAdjustment = useCallback(
    async (adjustmentInvoiceId: string) => {
      if (!invoiceId) {
        throw new Error('Invoice ID is required to link an adjustment');
      }

      await invoicesAdjustmentsLinkCreate(invoiceId, adjustmentInvoiceId);

      void mutate(getInvoiceMutator(invoiceId));
    },
    [invoiceId, mutate],
  );

  return linkAdjustment;
};

const useUnlinkAdjustment = (invoiceId: string | null) => {
  const { mutate } = useSWRConfig();

  const unlinkAdjustment = useCallback(
    async (adjustmentId: string) => {
      if (!invoiceId) {
        throw new Error('Invoice ID is required to unlink an adjustment');
      }

      await invoicesAdjustmentsUnlinkCreate(invoiceId, adjustmentId);

      void mutate(getInvoiceMutator(invoiceId));
    },
    [invoiceId, mutate],
  );

  return unlinkAdjustment;
};

// `code` mirrors the backend `generate-insights` 400 body; known values are
// `not_eligible` and `generation_failed`. `(string & {})` keeps the union open
// to unknown codes while still autocompleting the known ones.
export type RefreshInsightsError = {
  message: string;
  code: 'not_eligible' | 'generation_failed' | (string & {}) | null;
};

export const useRefreshInsights = (invoiceId: string | null) => {
  const { mutate } = useSWRConfig();
  const { showSnackbar } = useLAISnackbar();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<RefreshInsightsError | null>(null);

  // This hook instance is reused as the user navigates between invoices (the
  // panel isn't always remounted). Reset transient state when the viewed
  // invoice changes so one invoice's error/loading can't leak onto the next
  // and hide its review. Restores the per-invoice isolation the old
  // useSWRMutation (keyed on the invoice id) gave us.
  const [trackedInvoiceId, setTrackedInvoiceId] = useState(invoiceId);
  if (invoiceId !== trackedInvoiceId) {
    setTrackedInvoiceId(invoiceId);
    setError(null);
    setLoading(false);
  }

  // Lets an in-flight refresh detect that the user navigated away before it
  // resolved, so a late success/failure for a previous invoice doesn't write
  // onto the current one.
  const invoiceIdRef = useRef(invoiceId);
  invoiceIdRef.current = invoiceId;

  const refreshInsights = useCallback(async () => {
    if (!invoiceId) {
      throw new Error('Invoice ID is required to refresh insights');
    }
    const requestedInvoiceId = invoiceId;
    setLoading(true);
    setError(null);
    try {
      await invoicesGenerateInsightsCreate(requestedInvoiceId);
      // Pull fresh insights into the invoice cache so the panel renders them.
      // Generation already succeeded, so swallow revalidation failures rather
      // than surfacing them as a generation error (matches the other mutators).
      await mutate(getInvoiceMutator(requestedInvoiceId)).catch(() => {});
    } catch (err) {
      // Discard a late result for an invoice the user already navigated away from.
      if (invoiceIdRef.current !== requestedInvoiceId) return;
      if (err instanceof LiftAPIClientError) {
        const data = err.context.data as { error?: string; code?: string } | null;
        const code = data?.code ?? null;
        // Don't double-report errors the backend already owns: `not_eligible` is
        // an expected product state, and `generation_failed` is already captured
        // server-side with full context. Other/uncoded API errors still get captured.
        if (code !== 'not_eligible' && code !== 'generation_failed') {
          captureException(err);
        }
        // Surface the backend message verbatim; the global toast is intentionally skipped.
        setError({ message: data?.error ?? err.message, code });
      } else {
        // Unexpected/non-API error: capture it and keep the toast as a fallback.
        captureException(err);
        const message = err instanceof Error ? err.message : 'An unknown error occurred';
        showSnackbar(message, { variant: 'error' });
        setError({ message, code: null });
      }
    } finally {
      // Only clear loading if this is still the active invoice; the render-time
      // reset above already handles loading for a superseded one.
      if (invoiceIdRef.current === requestedInvoiceId) {
        setLoading(false);
      }
    }
  }, [invoiceId, mutate, showSnackbar]);

  return { refreshInsights, loading, error };
};

/**
 * Fetches the notes for an invoice.
 * @param invoiceId Invoice ID
 * @returns {notes: InvoiceNote[] | undefined, error: Error | undefined, loading: boolean, mutate: function}
 * @example
 * ```typescript
 *   const { notes, error, loading, mutate } = useInvoiceNotes('123');
 *   console.log(notes); // [{ id: 'mnj', content: 'example note', owner: {...}, invoice: '123', ... }]
 *   mutate(); // Revalidate notes
 * ```
 */
const useInvoiceNotes = (invoiceId: string | null) => {
  const {
    data,
    error,
    isLoading: loading,
    mutate,
  } = useSWR(invoiceId ? (['invoice', invoiceId, 'notes'] as const) : null, invoiceNotesFetcher);

  return { notes: data ?? [], error, loading, mutate };
};

export default function useInvoice(
  invoiceId: string | null,
  options?: Pick<SWRConfiguration, 'onError'>,
) {
  const {
    data,
    error,
    isLoading: loading,
  } = useSWR(invoiceId ? (['invoice', invoiceId] as const) : null, invoiceFetcher, options);

  const reviewInvoice = useReviewInvoice(invoiceId);
  const deleteInvoice = useInvoiceDelete();
  const invoiceNotes = useInvoiceNotes(invoiceId);
  const addNoteToInvoice = useAddInvoiceNote(invoiceId);
  const updateRelatedInvoice = useUpdateRelatedInvoice(invoiceId);
  const linkAdjustment = useLinkAdjustment(invoiceId);
  const unlinkAdjustment = useUnlinkAdjustment(invoiceId);

  return {
    invoice: data,
    error,
    loading,
    invoiceNotes,
    addNoteToInvoice,
    reviewInvoice,
    deleteInvoice,
    updateRelatedInvoice,
    linkAdjustment,
    unlinkAdjustment,
  };
}

export function useInvoicePreview(invoiceId: string | null) {
  // Cached document links may have expired, so show the invoice only once refetched.
  const { data, error, loading } = useRefetchedOnOpen(
    invoiceId ? (['invoice', invoiceId] as const) : null,
    invoiceFetcher,
  );

  return { invoice: data, error, loading };
}
